import { enableProdMode, provideZoneChangeDetection, APP_ID } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { USER_AGENT } from './app/tokens/user-agent.token';

// Swiper se registra perezoso en app/utils/swiper-loader (import dinámico) la
// primera vez que se necesita + un disparo temprano en app.component tras hidratar.
import { AppModule } from './app/app.module';
import { environment } from './environments/environment';
import { recargarSiFaltaUnTrozo } from './app/trozo-perdido';
// El import se evalúa antes que el cuerpo de este fichero, así que el módulo guarda la
// referencia al console.error DE VERDAD antes de que lo anule la linea de mas abajo.
import { depuracionActiva, mandarInformeDeError } from './app/informe-de-errores';


if (environment.production) {
  enableProdMode();
  // Silenciar la consola YA, antes del bootstrap. El APP_INITIALIZER de AppModule
  // también lo hace, pero corre más tarde y deja escapar un console.error temprano
  // del arranque (Lighthouse best-practices: errors-in-console → score 0 con uno solo).
  // Con `?debug=1` (o la clave mc-depurar en localStorage) no se silencia nada: es la
  // única forma de mirar un fallo en producción sin desplegar, y Lighthouse nunca pasa
  // por una URL con parámetros. Ver app/informe-de-errores.ts.
  if (typeof window !== 'undefined' && window.console && !depuracionActiva()) {
    window.console.error = window.console.warn = () => {};
  }
}

// Arranque tolerante al momento de carga: en fichas main.js se importa DINÁMICAMENTE
// tras pintar el hero (server.ts), y para entonces DOMContentLoaded ya disparó — con
// el addEventListener a secas el bootstrap no corría nunca.
// Un trozo de la aplicación que ya no existe: ver app/trozo-perdido.ts. Aquí se cubren
// los import dinámicos sueltos; los de Angular van por su ErrorHandler (app.module.ts).
// Los dos oyentes son además la ÚNICA red bajo lo que pasa antes del bootstrap: el
// ErrorHandler de Angular no existe todavía, y un fallo ahí es justo el que deja la página
// en blanco. Mismo muestreo y mismo tope que el de dentro (informe-de-errores.ts).
if (typeof window !== 'undefined') {
  window.addEventListener('error', (evento) => {
    const motivo = evento.error ?? evento.message;
    if (recargarSiFaltaUnTrozo(motivo)) return;
    mandarInformeDeError(motivo);
  });
  window.addEventListener('unhandledrejection', (evento) => {
    if (recargarSiFaltaUnTrozo(evento.reason)) return;
    mandarInformeDeError(evento.reason);
  });
}

const bootWhenReady = (fn: () => void) => {
  if (document.readyState !== 'loading') { fn(); return; }
  document.addEventListener('DOMContentLoaded', fn);
};
bootWhenReady(() => {
  // APP_ID 'mc' para que TransferState lea el <script id="mc-state">
  // que inyecta el servidor (social-meta renderTransferState): sin esto el navegador
  // usa el appId por defecto y no consume el estado → repite el fetch del arranque.
  // La migración a Angular 22 quitó BrowserModule.withServerTransition({appId}); esto
  // lo restituye (arregla también el TransferState de la ficha).
  platformBrowserDynamic().bootstrapModule(AppModule, {
    // provideClientHydration va en AppModule.providers (compartido cliente+servidor) para
    // que el servidor añada las anotaciones ngh; aquí solo lo específico del cliente.
    applicationProviders: [
      provideZoneChangeDetection(),
      // OJO: en MINÚSCULAS. Angular escribe el atributo de encapsulación como
      // _ngcontent-<APP_ID>-cNNN y el selector del CSS conserva las mayúsculas; el
      // parser de HTML, en cambio, pasa a minúsculas los nombres de atributo. En los
      // elementos SVG (que NO son HTML) Firefox compara el nombre del atributo con
      // mayúsculas y minúsculas, así que con 'serverApp' NINGUNA regla alcanzaba a los
      // <svg> venidos del SSR: los iconos se pintaban a su alto por defecto (150px) y
      // reventaban la maqueta. Chrome sí casaba y por eso no se veía.
      { provide: APP_ID, useValue: 'mc' },
      // UA en cliente para que isMobile coincida con el render de servidor (ver token).
      { provide: USER_AGENT, useFactory: () => (typeof navigator !== 'undefined' ? navigator.userAgent : '') },
    ],
  })
  // Si el bootstrap se cae no hay aplicación, no hay ErrorHandler y no queda nada en
  // pantalla: éste es el error más grave de todos y hasta hoy iba a un console.log mudo.
  .catch(err => { mandarInformeDeError(err); console.log(err); });
});
